import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  DeleteIconWrapper
} from '../styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { fetchSystemRoles, deleteSystemRole, resetSuperAdminActionState } from '../../../modules/superadmin';
import {
  selectSystemRoles,
  selectSystemRolesLoading,
  selectCreateRoleSuccess,
  selectCreateRoleError,
  selectUpdateRoleSuccess,
  selectUpdateRoleError,
  selectDeleteRoleSuccess,
  selectDeleteRoleError,
  selectSuperAdminActionMessage
} from '../../../modules/superadmin/selectors';
import { SystemRole } from '../../../modules/superadmin/types';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../components/common/AlertPopup';
import SystemRoleModal from './components/SystemRoleModal';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const SystemRolesPage: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const roles = useSelector(selectSystemRoles);
  const loading = useSelector(selectSystemRolesLoading);

  const createSuccess = useSelector(selectCreateRoleSuccess);
  const updateSuccess = useSelector(selectUpdateRoleSuccess);
  const deleteSuccess = useSelector(selectDeleteRoleSuccess);
  const createError = useSelector(selectCreateRoleError);
  const updateError = useSelector(selectUpdateRoleError);
  const deleteError = useSelector(selectDeleteRoleError);
  const actionMessage = useSelector(selectSuperAdminActionMessage);

  const actionSuccess = createSuccess || updateSuccess || deleteSuccess;
  const actionError = createError || updateError || deleteError;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SystemRole | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('system-roles');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'System Roles', path: '/super-admin/system-roles' },
    ]);
    setIsShow(true);
    dispatch(fetchSystemRoles());
  }, [setActivePage, setPath, setIsShow, dispatch]);

  useEffect(() => {
    if (actionSuccess) {
      setAlert({ message: actionMessage || 'Success', severity: 'success' });
      dispatch(fetchSystemRoles());
      dispatch(resetSuperAdminActionState());
      setIsModalOpen(false);
    }
    if (actionError) {
      setAlert({ message: actionError, severity: 'error' });
      dispatch(resetSuperAdminActionState());
    }
  }, [actionSuccess, actionError, actionMessage, dispatch]);

  const handleCreateClick = () => {
    setSelectedRole(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (role: SystemRole) => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteSystemRole(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">System Roles</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Role
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {Array.isArray(roles) && roles.length > 0 ? (
                roles.map((role: SystemRole) => (
                  <StyledTableRow key={role.role_id}>
                    <StyledTableCell>{role.role_id}</StyledTableCell>
                    <StyledTableCell>{role.role_name}</StyledTableCell>
                    <StyledTableCell>{role.role_description}</StyledTableCell>
                    <StyledTableCell align="right">
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Box sx={{ cursor: 'pointer', color: '#1976D2' }} onClick={() => handleEditClick(role)}>
                          <EditOutlinedIcon fontSize="small" />
                        </Box>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(role.role_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} align="center">
                    {loading ? 'Loading...' : 'No roles found.'}
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </MainContainer>

      <SystemRoleModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        role={selectedRole}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Role"
        message="Are you sure you want to delete this system role?"
      />
    </Box>
  );
};

export default SystemRolesPage;
